"use client";

import Image from "next/image";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import SatkomlekLogo from "@/app/image/satkomlekLogo.png";
import Button from "@/app/components/button";
import useLogin from "@/app/hooks/useLogin";
import { Turnstile } from "@marsidev/react-turnstile";
import Link from "next/link";

export default function Login() {
  const {
    email,
    password,
    loading,
    error,
    setEmail,
    setPassword,
    showPassword,
    togglePasswordVisibility,
    turnstileRef,
    siteKey,
    setTurnstileToken,
    turnstileToken,
    handleLogin,
  } = useLogin();

  const isDisabled = loading || !turnstileToken;

  return (
    <div className="relative min-h-screen w-full overflow-hidden ">
      {/* ===== Video Background (menggantikan LoginImage) ===== */}
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 z-0 h-full w-full object-cover opacity-45"
        onEnded={(e) => {
    
          const video = e.currentTarget;
          video.currentTime = 0;
          video.play().catch(() => {});
        }}
      >
        <source src="/video/Login.mov" type="video/quicktime" />
        {/* Fallback jika browser tidak mendukung .mov (disarankan sediakan versi .mp4) */}
        <source src="/video/Login.mp4" type="video/mp4" />
        Browser Anda tidak mendukung tag video.
      </video>

      {/* Overlay sangat tipis → latar tetap menembus kaca */}
      <div className="absolute inset-0 z-10 bg-linear-to-br from-black/35 via-black/25 to-black/45" />

      {/* ===== SVG Filter: refraksi tepi kaca (halus, tidak menggelombangkan latar) ===== */}
      <svg
        className="pointer-events-none absolute h-0 w-0"
        aria-hidden="true"
        focusable="false"
      >
        <filter id="glass-refraction" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.015 0.015"
            numOctaves="1"
            seed="7"
            result="noise"
          />
          <feGaussianBlur in="noise" stdDeviation="3" result="blurredNoise" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="blurredNoise"
            scale="8"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>

      <div className="relative z-20 flex min-h-screen items-center justify-center px-4 py-8 sm:px-6">
        <div className="group relative w-full max-w-xl">
          {/* Halo cahaya di belakang kartu */}
          <div className="pointer-events-none absolute -inset-1 rounded-[2.2rem] bg-gradient-to-br from-white/40 via-blue-200/10 to-blue-300/25 opacity-60 blur-md" />

          {/* ===== Kartu kaca ===== */}
          <div
            className="relative overflow-hidden rounded-[2rem] ring-1 ring-white/40"
            style={{
              boxShadow:
                "inset 0 1px 2px rgba(255,255,255,0.75), inset 0 -2px 4px rgba(255,255,255,0.12), inset 0 0 0 1px rgba(255,255,255,0.08), 0 12px 40px rgba(0,0,0,0.5)",
            }}
          >
            <div
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                backdropFilter: "blur(2px) saturate(160%) url(#glass-refraction)",
                WebkitBackdropFilter:
                  "blur(2px) saturate(160%) url(#glass-refraction)",
              }}
            />
            <div className="pointer-events-none absolute inset-0 z-0 bg-white/8" />
            <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-white/30 via-white/5 to-transparent" />
            <div className="pointer-events-none absolute inset-x-8 top-0 z-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
            <div className="pointer-events-none absolute inset-y-8 left-0 z-0 w-px bg-gradient-to-b from-white/60 via-white/10 to-transparent" />

            {/* Header */}
            <div className="relative z-10 flex items-center justify-between border-b border-white/15 px-6 py-5 sm:px-8">
              <div className="flex items-center gap-3">
                <div className="relative h-10 w-10 sm:h-11 sm:w-11">
                  <Image
                    src={SatkomlekLogo}
                    alt="Satkomlek Logo"
                    fill
                    className="object-contain drop-shadow-lg"
                  />
                </div>
                <h1 className="text-lg font-semibold tracking-wide text-white drop-shadow-md sm:text-xl">
                  Satkomlek TNI
                </h1>
              </div>
              <span className="text-sm font-medium text-white/90 drop-shadow sm:text-base">
                Masuk
              </span>
            </div>

            <form
              onSubmit={handleLogin}
              className="relative z-10 px-6 py-8 sm:px-10 sm:py-10"
            >
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-white drop-shadow-md sm:text-3xl">
                  Selamat Datang
                </h2>
                <p className="mt-2 text-sm text-white/75 drop-shadow">
                  Silakan masuk ke Sistem E-Kodefikasi
                </p>
              </div>

              {error && (
                <div className="mb-5 rounded-xl border border-red-400/40 bg-red-500/15 px-4 py-3 text-center text-sm text-red-100 backdrop-blur-sm">
                  {error}
                </div>
              )}

              <div className="group/field relative mb-5">
                <FaUser className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-white/55 transition-colors duration-200 group-focus-within/field:text-blue-300" />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="email"
                  className="
                    w-full rounded-xl border border-white/30 bg-white/10 py-3.5 pl-11 pr-4
                    text-white placeholder-white/50 outline-none backdrop-blur-md
                    shadow-[inset_0_1px_2px_rgba(255,255,255,0.3),inset_0_-1px_2px_rgba(0,0,0,0.1)]
                    transition-all duration-200
                    focus:border-blue-400/70 focus:bg-white/15 focus:ring-2 focus:ring-blue-400/30
                  "
                />
              </div>

              <div className="group/field relative mb-3">
                <FaLock className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-white/55 transition-colors duration-200 group-focus-within/field:text-blue-300" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Kata Sandi"
                  autoComplete="current-password"
                  className="
                    w-full rounded-xl border border-white/30 bg-white/10 py-3.5 pl-11 pr-12
                    text-white placeholder-white/50 outline-none backdrop-blur-md
                    shadow-[inset_0_1px_2px_rgba(255,255,255,0.3),inset_0_-1px_2px_rgba(0,0,0,0.1)]
                    transition-all duration-200
                    focus:border-blue-400/70 focus:bg-white/15 focus:ring-2 focus:ring-blue-400/30
                  "
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer text-white/55 hover:text-white/85"
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>

              <Link href="/pages/reset-request">
                <div className="mb-6 flex justify-end">
                  <button
                    type="button"
                    className="text-sm font-medium text-blue-200 drop-shadow transition-colors hover:text-blue-100"
                  >
                    Lupa kata sandi?
                  </button>
                </div>
              </Link>

              <div className="mb-5 flex min-h-17.5 justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={siteKey}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken("")}
                  onError={() => setTurnstileToken("")}
                />
              </div>

              <Button
                text={loading ? "Memproses..." : "Masuk"}
                styleButton={`w-full py-3.5 rounded-xl font-semibold transition-all duration-300 ${
                  isDisabled
                    ? "bg-white/15 cursor-not-allowed opacity-60 ring-1 ring-white/20"
                    : "bg-gradient-to-r from-blue-500/90 to-blue-400/90 hover:from-blue-400 hover:to-blue-500 ring-1 ring-white/40 shadow-lg shadow-blue-500/30 active:scale-[0.98]"
                }`}
                styleText="text-white text-base drop-shadow-md"
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}